import type { ReactNode } from 'react';
import { matchPath } from 'react-router';
import type { Params } from 'react-router';

import HomeIcon from './HomeIcon';

/**
 * Data available to breadcrumb title/label/href resolvers while
 * building the header trail for the current route.
 */
export interface BreadcrumbResolveContext {
    /** Params of the matched route pattern (e.g. departmentId). */
    params: Params<string>;
    /** Name of the active organization, once loaded. */
    orgName?: string | undefined;
    /** Name of the active department, once loaded. */
    departmentName?: string | undefined;
    /** Name of the active task, once loaded (bemanningsplaner/:taskId). */
    taskName?: string | undefined;
    t: (path: string, fallback: string) => string;
}

export interface BreadcrumbSegmentConfig {
    /**
     * Segment label; resolves dynamically for route data (e.g. org
     * name). May be any ReactNode, e.g. an icon-only segment.
     */
    label:
        | string
        | ReactNode
        | ((context: BreadcrumbResolveContext) => string | ReactNode);
    /** Link target; omit for plain, non-clickable segments. */
    href?: (context: BreadcrumbResolveContext) => string;
}

export interface BreadcrumbRouteConfig {
    /** Route pattern matched against the current pathname (react-router syntax). */
    pattern: string;
    /** Current page title, rendered above the trail by the Breadcrumb. */
    title: string | ((context: BreadcrumbResolveContext) => string);
    /**
     * Ancestor trail segments, rendered left to right below the title.
     * The current page itself is not part of the trail (it is the title).
     */
    segments: BreadcrumbSegmentConfig[];
    /** Help text shown when hovering the title. */
    activeTooltip?: string | ((context: BreadcrumbResolveContext) => string);
}

export interface BreadcrumbRouteMatch {
    route: BreadcrumbRouteConfig;
    params: Params<string>;
}

/**
 * Root trail segment, shown as a non-clickable home icon (no link).
 * The icon is the label itself: rendering it via the `Icon` prop would
 * add an extra empty label span (with its flex gap) before the
 * separator.
 */
const homeSegment: BreadcrumbSegmentConfig = {
    label: <HomeIcon />,
};

/** Active organization name, linking back to the organization overview. */
const orgSegment: BreadcrumbSegmentConfig = {
    label: ({ orgName }) => orgName ?? '',
    href: () => '/',
};

/**
 * Segments of the department tree
 * (`/departments/:departmentId/...`, see
 * `src/pages/departments/config.ts`).
 */

/**
 * Active department name, linking to the department home
 * (dashboard) — the former fallback to the employee list was the
 * placeholder landing page before the department home existed.
 */
const departmentSegment: BreadcrumbSegmentConfig = {
    label: ({ departmentName }) => departmentName ?? '',
    href: ({ params }) => `/departments/${params.departmentId}`,
};

/** Employee list section, linkable from its sub-pages. */
const employeesSegment: BreadcrumbSegmentConfig = {
    label: 'Ansatte',
    href: ({ params }) => `/departments/${params.departmentId}/employees`,
};

/** Staffing plan list section, linkable from its sub-pages. */
const plansSegment: BreadcrumbSegmentConfig = {
    label: 'Bemanningsplaner',
    href: ({ params }) => `/departments/${params.departmentId}/tasks`,
};

/** Organization home, mounted both at `/` and at `/organization`. */
const organizationHomeRoute: Omit<BreadcrumbRouteConfig, 'pattern'> = {
    title: ({ t }) => t('my_org.title', 'Min organisasjon'),
    segments: [],
    activeTooltip: ({ t }) =>
        t(
            'my_org.tooltip.label',
            'Du er nå i oversikten over de avdelinger du har tilgang til i Dynamon. Dersom ingen avdelinger er synlige kan du opprette en avdeling',
        ),
};

/**
 * Organization settings: one page for all its tabs. The `:tab` URL
 * segment (internal page state, synced for deep linking) must not
 * change the breadcrumb title.
 */
const organizationSettingsRoute: Omit<BreadcrumbRouteConfig, 'pattern'> = {
    title: ({ t }) => t('organization_settings.label', 'Innstillinger'),
    segments: [homeSegment, orgSegment],
};

/**
 * Header breadcrumb trails per route: `title` is the current page and
 * `segments` its ancestor trail
 * (home icon › organization › department › parent section).
 *
 * Order matters: the first matching pattern wins, so static segments
 * (e.g. `ansatte/ny`) are listed before dynamic ones
 * (e.g. `ansatte/:employeeId`).
 */
export const BREADCRUMB_ROUTES: BreadcrumbRouteConfig[] = [
    {
        pattern: '/',
        ...organizationHomeRoute,
    },
    {
        pattern: '/organization',
        ...organizationHomeRoute,
    },
    {
        pattern: '/organization/select',
        title: ({ t }) => t('organization_select.title', 'Velg organisasjon'),
        segments: [homeSegment],
    },
    {
        pattern: '/organization/settings/:tab',
        ...organizationSettingsRoute,
    },
    {
        pattern: '/organization/settings',
        ...organizationSettingsRoute,
    },
    // Profile — disabled for now (path reserved as `/profile`);
    // re-add the breadcrumb entry together with the route.
    //
    // Department tree (`/departments/:departmentId/...`).
    // Order matters: the first matching pattern wins, so static
    // segments (e.g. `employees/new`) are listed before dynamic ones
    // (e.g. `employees/:employeeId`).
    {
        // Department dashboard (mock) — the department root page.
        pattern: '/departments/:departmentId',
        title: ({ departmentName }) => departmentName ?? '',
        segments: [homeSegment, orgSegment],
    },
    {
        pattern: '/departments/:departmentId/employees',
        title: 'Ansatte',
        segments: [homeSegment, orgSegment, departmentSegment],
        activeTooltip:
            'Her finner du oversikt over alle ansatte i din avdeling og kan legge inn eller slette ansatte, samt spesifisere hvordan de skal arbeide',
    },
    {
        pattern: '/departments/:departmentId/employees/table',
        title: 'Rediger ansatte',
        segments: [
            homeSegment,
            orgSegment,
            departmentSegment,
            employeesSegment,
        ],
        activeTooltip:
            'Opprett ansattliste:<br />Velger "Legg til flere rader"<br />Sett inn det antall ansatte du vil opprette<br />Det er nå to måter å opprette ansatte på:<br />Kopier navn, stilling eller stillingsprosent fra et excel ark. Marker de kolonnene du vil kopiere, høyreklikk og velg "kopier".<br />Sett markøren i den første gule cellen under navn i den tomme ansattlisten i Dynamon. Trykk ctrl+V. Ansattlisten er da opprettet i Dynamon<br />Legg inn en og en ansatt<br /><br />Dersom du skal legge til flere ansatte, trykk på"Legg til flere ansatte"',
    },
    {
        pattern: '/departments/:departmentId/employees/new',
        title: 'Legg til ny ansatt',
        segments: [
            homeSegment,
            orgSegment,
            departmentSegment,
            employeesSegment,
        ],
    },
    {
        pattern: '/departments/:departmentId/employees/wages',
        title: 'Timelønn',
        segments: [
            homeSegment,
            orgSegment,
            departmentSegment,
            employeesSegment,
        ],
        activeTooltip: 'Her kan du sette opp ansattes timelønn',
    },
    {
        pattern: '/departments/:departmentId/employees/settings',
        title: 'Innstillinger',
        segments: [
            homeSegment,
            orgSegment,
            departmentSegment,
            employeesSegment,
        ],
    },
    {
        // Employee settings tabs: the `:tab` URL segment must not
        // change the breadcrumb title (org settings `:tab` pattern).
        pattern: '/departments/:departmentId/employees/settings/:tab',
        title: 'Innstillinger',
        segments: [
            homeSegment,
            orgSegment,
            departmentSegment,
            employeesSegment,
        ],
    },
    {
        pattern: '/departments/:departmentId/employees/:employeeId',
        title: 'Rediger ansatt',
        segments: [
            homeSegment,
            orgSegment,
            departmentSegment,
            employeesSegment,
        ],
    },
    {
        pattern: '/departments/:departmentId/planning',
        title: 'Planlegging',
        segments: [homeSegment, orgSegment, departmentSegment],
    },
    {
        // Single `:tab` route: switching planner tabs only changes the
        // param, so the breadcrumb title stays constant (org settings
        // `:tab` pattern).
        pattern: '/departments/:departmentId/planning/:tab',
        title: 'Planlegging',
        segments: [homeSegment, orgSegment, departmentSegment],
    },
    {
        pattern: '/departments/:departmentId/tasks',
        title: 'Bemanningsplaner',
        segments: [homeSegment, orgSegment, departmentSegment],
        activeTooltip:
            'Her finner du liste over alle bemanningsplaner du har laget',
    },
    {
        pattern: '/departments/:departmentId/tasks/:taskId/result',
        title: 'Resultat',
        segments: [homeSegment, orgSegment, departmentSegment, plansSegment],
    },
    {
        // Result tabs: the `:tab` URL segment must not change the
        // breadcrumb title (org settings `:tab` pattern).
        pattern: '/departments/:departmentId/tasks/:taskId/result/:tab',
        title: 'Resultat',
        segments: [homeSegment, orgSegment, departmentSegment, plansSegment],
    },
    {
        pattern: '/departments/:departmentId/tasks/:taskId',
        title: ({ taskName }) => taskName ?? '',
        segments: [homeSegment, orgSegment, departmentSegment, plansSegment],
        activeTooltip:
            'Her gjør du overordnede innstillinger iht. lokale avtaler mellom arbeidsgiver- og arbeidstakerorganisasjoner',
    },
    {
        pattern: '/departments/:departmentId/settings',
        title: 'Avdelingsoppsett',
        segments: [homeSegment, orgSegment, departmentSegment],
        activeTooltip:
            'I avdelingsoppsett kan du definere navnet på stillinger og spesialkompetanser. Du kan også legge inn de bevegelige helligdagene, samt hovedferien i avdelingen',
    },
    {
        // Settings tabs: the `:tab` URL segment must not change the
        // breadcrumb title (org settings `:tab` pattern).
        pattern: '/departments/:departmentId/settings/:tab',
        title: 'Avdelingsoppsett',
        segments: [homeSegment, orgSegment, departmentSegment],
        activeTooltip:
            'I avdelingsoppsett kan du definere navnet på stillinger og spesialkompetanser. Du kan også legge inn de bevegelige helligdagene, samt hovedferien i avdelingen',
    },
];

/**
 * Resolves the breadcrumb route config matching the given pathname.
 * The first matching pattern wins (see `BREADCRUMB_ROUTES` ordering).
 */
export const matchBreadcrumbRoute = (
    pathname: string,
): BreadcrumbRouteMatch | undefined => {
    for (const route of BREADCRUMB_ROUTES) {
        const match = matchPath(route.pattern, pathname);
        if (match) {
            return { route, params: match.params };
        }
    }

    return undefined;
};
